import _ from "lodash";
import { connect } from "react-redux";
import moment from "moment";
import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import { ADVANCED_CARE_ACTIVITY_INITIAL_VALUES } from "@/features/notes/(v2)/(advanced-care)/constants";
import {
  buildInterventionAndPlanFormFromGoalAndObjectives,
  checkInterventionAndPlanErrors,
} from "@/features/notes/(v2)/helpers/intervention-and-plan-v2-form-helpers";
import { carePlanRequests } from "@/api/(requests)/carePlan";
import { createEmptyNote } from "@/features/notes/(v2)/helpers/workflow-functions";
import { fetchNecessaryNotesByWorkflow } from "@/utils/noteUtils";
import { patientKeys } from "@/features/patient/api/keyFactory";
import { ProviderEnum } from "@/core/constants";
import { providerRequests } from "@/api/(requests)/provider";
import { ProviderWelcomeStep } from "@/features/cpt-workflow/components/ProviderWelcomeStep";
import { queryClient } from "@/providers/QueryProvider";
import { sharedRequests } from "@/api/(requests)/shared";
import Spinner from "@/components/UI/Spinner";

import { VideoViewBase } from "../VideoViewBase";

class ProviderVideoWorkflowStep extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: -1,
      fullScreen: false,
      lastNote: null,
      markerData: [],
      noteErrors: {},
      workflow: this.props.workflow,
    };
  }

  async componentDidMount() {
    let callDetails = this.props.callDetails;
    const context = { showLoader: false };
    const urlParams = { patientId: callDetails.patient_username };
    let patientDetails = (await sharedRequests.fetch_patient_details({ context, urlParams })).user;

    // Seed react-query's cache so components like MemberHeader (rendered
    // further down the call workflow, e.g. AdvancedCareAssessmentStep) don't
    // re-fetch data we already have and pop in with a visible delay.
    queryClient.setQueryData(patientKeys.details(callDetails.patient_username), patientDetails);

    let workflowDetails = this.props.workflow.details;
    let currentStep = workflowDetails.startingStep;

    let notes = [];
    let questionSet;
    let processedQuestionSet;
    let lastNotesOther = {};
    let lastNote = null;
    const validNoteTypes = [
      "Video Consultation",
      "Check-in",
      "Care Coordination",
      "Initial Clinical Assessment",
      "Care Communication",
      "Case Management",
      null,
    ];

    if (
      workflowDetails.viewLastNoteSelf ||
      workflowDetails.viewLastNoteOther !== null ||
      workflowDetails.createNote
    ) {
      const lastCompletedNoteByProviderIdQueryParams = {
        completed_date_time_exists: true,
        is_not_patient_no_show: true,
        limit_one_per_provider_type: true,
        note_state_is_not: "draft",
        note_types: validNoteTypes.join(","),
        provider_id: callDetails.provider_username,
        version: workflowDetails.noteVersion,
      };

      notes = await sharedRequests.fetch_patient_progress_notes({
        context,
        searchParams: lastCompletedNoteByProviderIdQueryParams,
        urlParams,
      });

      lastNote = notes ? notes[0] : null;

      const lastCompletedNotesByOtherProvidersQueryParams = {
        completed_date_time_exists: true,
        is_not_patient_no_show: true,
        limit_one_per_provider_type: true,
        note_state_is_not: "draft",
        note_types: validNoteTypes.join(","),
        provider_type_begins_with: workflowDetails.viewLastNoteOther
          ? workflowDetails.viewLastNoteOther
          : null,
        version: workflowDetails.noteVersion,
      };

      let lastCompletedNotesByOtherProvidersResponse =
        await sharedRequests.fetch_patient_progress_notes({
          context,
          searchParams: lastCompletedNotesByOtherProvidersQueryParams,
          urlParams,
        });

      lastNotesOther = fetchNecessaryNotesByWorkflow(
        workflowDetails,
        lastCompletedNotesByOtherProvidersResponse,
      );
      const data = {
        callId: callDetails["callId"],
      };

      questionSet = await sharedRequests.get_patient_call_question_sets({ context, data });
      processedQuestionSet = callDetails.patient_question_sets
        ? callDetails.patient_question_sets
        : questionSet;
    }

    let progressNote;
    let patientDiagnosis = null;

    if (workflowDetails.createNote) {
      let existingNotes = await sharedRequests.fetch_patient_progress_notes({
        context,
        searchParams: {
          call_id: callDetails.callId,
        },
        urlParams,
      });

      let existingNote = existingNotes && existingNotes.length === 1 ? existingNotes[0] : null;

      patientDiagnosis = await sharedRequests.get_patient_diagnosis({ context, urlParams });
      queryClient.setQueryData(
        patientKeys.diagnosis(callDetails.patient_username),
        patientDiagnosis,
      );
      if (existingNote) {
        progressNote = existingNote;
      } else {
        progressNote = createEmptyNote(callDetails, workflowDetails.noteVersion, patientDiagnosis);
      }

      if (workflowDetails.noteEnricher) {
        let goalAndObjectiveForm = null;

        if (!existingNote && progressNote.provider_type === ProviderEnum.BEHAVIORAL) {
          const goalsAndObjectives = await carePlanRequests.get_goals_and_objectives({
            urlParams,
          });

          goalAndObjectiveForm = buildInterventionAndPlanFormFromGoalAndObjectives(
            goalsAndObjectives,
            progressNote.provider_type,
          );
        }

        progressNote.content = workflowDetails.noteEnricher(
          patientDetails,
          processedQuestionSet,
          workflowDetails.relevantPatientQuestionSections,
          { existingNote, lastNote, lastNotesOther },
          patientDiagnosis,
          goalAndObjectiveForm,
        );
      }

      if (existingNote && !existingNote.completed_date_time && !existingNote.signature) {
        progressNote = await this.saveNoteHandler(progressNote);
      } else if (!existingNote) {
        const urlParams = { patientId: this.props.callDetails.patient_username };

        progressNote = await providerRequests.post_progress_note({
          context,
          data: progressNote,
          urlParams,
        });
      }
    } else if (workflowDetails.createClinicalAssessmentNote) {
      const existingClinicalAssessment = await sharedRequests.fetch_patient_progress_notes({
        context,
        searchParams: {
          note_types: "Initial Clinical Assessment",
          provider_id: callDetails.provider_username,
        },
        urlParams,
      });

      if (Array.isArray(existingClinicalAssessment) && existingClinicalAssessment.length > 0) {
        progressNote = existingClinicalAssessment[0];
      } else {
        progressNote = await providerRequests.post_patient_clinical_assessment({
          data: {},
          searchParams: { type: "initial" },
          urlParams: { patientId: this.props.callDetails.patient_username },
        });
      }
    } else if (workflowDetails.createAcaaAssessment) {
      // Two-step call, step 1: create (or reuse) the draft ACP Activity
      // Assessment note for this call. The backend prefills patient info,
      // session date/time, and attendance defaults from the call record —
      // dedups by call_id server-side too, so this is safe to re-run.
      let existingNotes = await sharedRequests.fetch_patient_progress_notes({
        context,
        searchParams: {
          call_id: callDetails.callId,
          note_types: "Advanced Care Activity",
        },
        urlParams,
      });

      let existingNote = existingNotes && existingNotes.length === 1 ? existingNotes[0] : null;

      patientDiagnosis = await sharedRequests.get_patient_diagnosis({ context, urlParams });
      queryClient.setQueryData(
        patientKeys.diagnosis(callDetails.patient_username),
        patientDiagnosis,
      );

      if (existingNote) {
        progressNote = existingNote;
      } else {
        progressNote = await providerRequests.post_progress_note({
          context,
          data: {
            call_id: callDetails.callId,
            content: {},
            note_type: "Advanced Care Activity",
          },
          urlParams,
        });
      }

      // The backend deliberately leaves clinical-judgment fields (activity_purpose,
      // recommended_next, etc.) unset on creation/prefill rather than defaulting them
      // (see progress_notes.py) - it's on the FE to seed the shape AdvancedCareActivityForm
      // expects, same as the manual "New Note" entry point does with this same constant.
      // Real values (prefilled or previously saved) always win over the defaults. This
      // must merge the whole constant (not just `.content`) - top-level fields like
      // `signature` are also left unset by the backend when absent from the request.
      progressNote = _.merge({}, ADVANCED_CARE_ACTIVITY_INITIAL_VALUES, progressNote);
    }

    let addendums;

    if (lastNote) {
      const patientId = callDetails.patient_username;
      const urlParams = { patientId };

      const data = {
        note_id: lastNote.id,
        patient_id: patientId,
      };
      const addendumsResponse = await sharedRequests.get_note_addendums({
        context,
        data,
        urlParams,
      });

      addendums = addendumsResponse.addendums;
    }

    this.setState({
      addendums,
      callDetails,
      currentStep,
      lastNote,
      lastNotesOther,
      notes,
      patientDetails,
      patientDiagnosis,
      progressNote,
      refresh: true,
      workflowDetails,
    });

    if (currentStep >= 0 && this.props.workflow.steps[currentStep].startCall) {
      if (!this.props.activeRoom) {
        await this.props.startCall();
      } else {
        this.props.attachRemoteTracks();
        this.props.attachLocalTracks();
      }
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      !prevProps.callDetails.patient_question_sets &&
      this.props.callDetails.patient_question_sets
    ) {
      const {
        lastNote,
        lastNotesOther,
        patientDetails,
        patientDiagnosis,
        progressNote,
        workflowDetails,
      } = this.state;

      let progressNoteCopy = _.cloneDeep(progressNote);

      if (workflowDetails.noteEnricher) {
        progressNoteCopy.content = workflowDetails.noteEnricher(
          patientDetails,
          this.props.callDetails.patient_question_sets,
          workflowDetails.relevantPatientQuestionSections,
          { existingNote: progressNote, lastNote, lastNotesOther },
          patientDiagnosis,
        );

        await this.saveNoteHandler(progressNoteCopy);
      }

      this.setState({
        callDetails: this.props.callDetails,
        progressNote: progressNoteCopy,
        refresh: false,
      });

      this.setState({
        refresh: true,
      });
    }
  }

  /**
   * changes the current step of the workflow
   *
   * @param {*} step the step number to jump to
   * @param {*} updatedProgressNote optional, passing in data here means
   * that the progress note saved in this components state should be ignored
   * and should be updated after the note is saved
   */
  jumpWorkflowStep = async (step, updatedProgressNote) => {
    let nextStep = step;

    this.setState({
      currentStep: nextStep,
    });

    if (this.props.workflow.steps[nextStep].startCall) {
      if (!this.props.activeRoom) {
        await this.props.startCall();
      } else {
        this.props.attachRemoteTracks();
        this.props.attachLocalTracks();
      }
    }

    if (this.props.workflow.steps[nextStep].endCall && this.props.activeRoom) {
      await this.endCall();
    }

    if (this.props.workflow.steps[nextStep].saveNote) {
      const progressNote = updatedProgressNote ?? { ...this.state.progressNote };

      progressNote["content"]["call_timer"] = this.props.timer;
      if (!progressNote.completed_date_time && !progressNote.signature) {
        this.saveNoteHandler(progressNote);
        if (updatedProgressNote) {
          this.setState({ progressNote });
        }
      }
    }
  };

  endCall = async () => {
    let lastStep = this.props.workflow.steps.length - 1;

    this.setState({
      currentStep: lastStep,
    });

    await this.props.endCall(this.state.workflow.details.endCallShouldRoute);
  };

  /**
   * helper function that updates the content
   * of the note WITHOUT modifying state and returns the update
   *
   * @param {*} currentNote
   * @param {*} key
   * @param {*} value
   * @param {*} nested
   * @returns
   */
  updateNoteContent = (currentNote, key, value, nested = true) => {
    let clonedNote = _.cloneDeep(currentNote);

    if (nested) {
      clonedNote.content[key] = _.cloneDeep(value);
    } else {
      clonedNote[key] = _.cloneDeep(value);
    }

    return clonedNote;
  };

  noteEditHandler = (key, value, nested = true) => {
    this.setState((prevState) => {
      return {
        progressNote: this.updateNoteContent(prevState.progressNote, key, value, nested),
      };
    });
  };

  signNote = async () => {
    let { callDetails, progressNote } = this.state;

    progressNote["signature"] = callDetails?.provider_name;
    progressNote["completed_date_time"] = moment().format();
    this.setState({ progressNote });
  };

  saveNoteHandler = async (progressNote) => {
    try {
      const urlParams = { patientId: this.props.callDetails.patient_username };

      // showLoader intentionally left enabled here (unlike other calls in
      // this file) — long-running "sign & submit" request with no other
      // in-flight feedback; the full-screen overlay also blocks click-through
      // on the button, guarding against rapid double-submits.
      return await providerRequests.put_progress_note({
        data: progressNote,
        urlParams,
      });
    } catch (e) {
      console.log(e);
      this.props.history.push(this.props.authRedirectPath);
    }
  };

  noteDraftValidation = () => {
    const hasInterventionAndPlanError = checkInterventionAndPlanErrors(
      this.state.progressNote?.content,
    );

    return !hasInterventionAndPlanError;
  };

  noteDraftSubmitHandler = async () => {
    if (!this.noteDraftValidation()) {
      return;
    }
    let progressNote = this.state.progressNote;

    await this.saveNoteHandler(progressNote);
    this.props.history.push(this.props.authRedirectPath);
  };

  noteValidation = () => {
    let progressNote = this.state.progressNote;
    const noteErrors = {};

    if (
      !progressNote["ICD_10_axis_1_diagnosis"] ||
      progressNote["ICD_10_axis_1_diagnosis"].length === 0
    ) {
      noteErrors["ICD_10_axis_1_diagnosis"] = "ICD-10 Primary Diagnosis is required";
    }

    if (!progressNote["call_type"]) {
      noteErrors["call_type"] = "CPT Code is required";
    }

    if (
      progressNote.provider_type === ProviderEnum.PRESCRIBE &&
      !progressNote["service_location"]
    ) {
      noteErrors["service_location"] = "Service Location is required";
    }

    if (checkInterventionAndPlanErrors(progressNote?.content)) {
      // the error message here has no impact, the error is displayed by
      // formik
      noteErrors["goals_objectives_progress_v2"] = "goals_objectives_progress_v2_error";
    }

    this.setState({ noteErrors });

    return Object.keys(noteErrors).length === 0;
  };

  noteSubmitHandler = async () => {
    if (!this.noteValidation()) {
      return;
    }
    await this.signNote();
    let progressNote = this.state.progressNote;

    progressNote["note_state"] = "complete";
    await this.saveNoteHandler(progressNote);
    this.props.history.push(this.props.authRedirectPath);
  };

  generateHeader = () => {
    let header = null;

    if (this.props.workflow.steps.length !== this.state.currentStep) {
      let headerText = this.props.workflow.steps[this.state.currentStep].header;

      if (headerText) {
        header = <div className="text-white fs-14 fs-lg-18 tw:font-bold">{headerText}</div>;
      }
    }

    return header;
  };

  generateStep = () => {
    let content = <div />;

    if (this.props.workflow.steps.length !== this.state.currentStep) {
      let step = this.props.workflow.steps[this.state.currentStep];
      let componentProps = {
        endCall: this.endCall,
        jumpWorkflowStep: this.jumpWorkflowStep,
        noteDraftSubmitHandler: this.noteDraftSubmitHandler,
        noteEditHandler: this.noteEditHandler,
        noteSubmitHandler: this.noteSubmitHandler,
        props: this.props,
        state: this.state,
        step: step,
        updateNoteContent: this.updateNoteContent,
      };

      content = <step.component {...componentProps} />;
    }

    return content;
  };

  render() {
    if (!this.state.callDetails) {
      return <Spinner />;
    }

    if (this.state.currentStep === -1) {
      return <ProviderWelcomeStep startWorkflow={this.jumpWorkflowStep} {...this.props} />;
    }

    return (
      <VideoViewBase
        content={this.generateStep()}
        currentStep={this.state.currentStep}
        header={this.generateHeader()}
        workflow={this.props.workflow}
        {...this.props}
      />
    );
  }
}

const mapStateToProps = (state) => {
  return {
    authRedirectPath: state.auth.authRedirectPath,
  };
};

export default withRouter(connect(mapStateToProps)(ProviderVideoWorkflowStep));
